import React, { useEffect, useMemo, useState } from "react";
import {
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";

import {
    holidayDataDelete,
    HolidayDataSubmit,
    HolidayUpdateData,
} from "../../redux/person/actionCreator";
import { getLeavesDataRequestData } from "../../redux/mail/actionCreator";

const { Title } = Typography;

const HOLIDAY_TYPES = [
    { value: "PUBLIC", label: "PUBLIC" },
    { value: "OPTIONAL", label: "OPTIONAL" },
];

export default function HolidaysPage() {
    const dispatch = useDispatch();
    const [form] = Form.useForm();

    const { holidays, loading } = useSelector((state) => ({
        holidays: state?.GetLeaveData?.data ?? [],
        loading: state?.GetLeaveData?.loading ?? false,
    }));

    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState("create");
    const [editingRow, setEditingRow] = useState(null);

    useEffect(() => {
        dispatch(getLeavesDataRequestData());
    }, [dispatch]);

    const dataSource = useMemo(() => {
        const rows = Array.isArray(holidays) ? holidays : [];

        return rows
            .map((r) => ({
                ...r,
                key: r.id,
                dateStr: r?.date ? dayjs(r.date).format("YYYY-MM-DD") : "",
                month: r?.date ? dayjs(r.date).format("MMMM") : "",
                day: r?.date ? dayjs(r.date).format("ddd").toUpperCase() : "",
            }))
            .sort((a, b) => {
                const da = a?.date ? new Date(a.date).getTime() : 0;
                const db = b?.date ? new Date(b.date).getTime() : 0;
                if (db === da) return (b.id ?? 0) - (a.id ?? 0);
                return db - da;
            });
    }, [holidays]);

    const openCreate = () => {
        setMode("create");
        setEditingRow(null);
        form.resetFields();
        setModalOpen(true);
    };

    const openEdit = (row) => {
        setMode("edit");
        setEditingRow(row);

        form.setFieldsValue({
            name: row.name,
            type: row.type,
            date: row.date ? dayjs(row.date) : null,
        });

        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setEditingRow(null);
        form.resetFields();
    };

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();

            const payload = {
                name: values.name?.trim(),
                type: values.type,
                date: values.date ? values.date.toDate() : null,
            };

            if (mode === "create") {
                await dispatch(HolidayDataSubmit(payload));
            } else {
                await dispatch(
                    HolidayUpdateData({
                        ...payload,
                        id: editingRow?.id,
                    })
                );
            }

            closeModal();
            await dispatch(getLeavesDataRequestData());
        } catch (err) {
            if (err?.message) message.error(err.message);
        }
    };

    const onDelete = async (id) => {
        try {
            await dispatch(holidayDataDelete(id));
        } catch (err) {
            if (err?.message) message.error(err.message);
        }
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "dateStr",
            key: "dateStr",
            width: 140,
            sorter: (a, b) => new Date(b.date) - new Date(a.date), // desc
            defaultSortOrder: "descend",
        },
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            width: 90,
        },
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            width: 140,
        },
        {
            title: "Festival / Observance",
            dataIndex: "name",
            key: "name",
            render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
        },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            width: 120,
            render: (type) => (
                <Tag color={type === "OPTIONAL" ? "purple" : "blue"}>{type}</Tag>
            ),
            filters: [
                { text: "PUBLIC", value: "PUBLIC" },
                { text: "OPTIONAL", value: "OPTIONAL" },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: "Action",
            key: "action",
            width: 120,
            render: (_, row) => (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
                    <Popconfirm
                        title="Delete this holiday?"
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => onDelete(row.id)}
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 16 }}>
            <Card bordered={false} style={{ borderRadius: 12 }} bodyStyle={{ padding: 16 }}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        marginBottom: 12,
                    }}
                >
                    <Title level={4} style={{ margin: 0 }}>
                        Holidays
                    </Title>

                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Create Holiday
                    </Button>
                </div>

                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={dataSource}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    rowKey="id"
                />
            </Card>

            <Modal
                open={modalOpen}
                title={mode === "create" ? "Create Holiday" : "Update Holiday"}
                onCancel={closeModal}
                onOk={onSubmit}
                okText={mode === "create" ? "Create" : "Update"}
                destroyOnClose
            >
                <Form layout="vertical" form={form}>
                    <Form.Item
                        label="Festival / Observance"
                        name="name"
                        rules={[{ required: true, message: "Name is required" }]}
                    >
                        <Input placeholder="Enter holiday name" />
                    </Form.Item>

                    <Form.Item
                        label="Date"
                        name="date"
                        rules={[{ required: true, message: "Date is required" }]}
                    >
                        <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>

                    <Form.Item
                        label="Type"
                        name="type"
                        rules={[{ required: true, message: "Type is required" }]}
                    >
                        <Select placeholder="Select type" options={HOLIDAY_TYPES} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
